import { Button, FormLayout, TextInput } from 'components/common'
import { Switch } from 'components/common/switch'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { yupResolver } from '@hookform/resolvers/yup'
import { ConfigurationWithId } from 'types/configuration'
import { getNestedErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import { handleError } from 'utils/helpers/handleError'
import { updateAlertConfig } from 'utils/api/configApi'
import {
  alertConfigSchema,
  AlertConfigFormFields,
} from 'utils/schema/alertConfigSchema'
import {
  ALERT_NUMERIC_FIELD_KEYS,
  buildAlertPayload,
  // configRootFields,
  configurationsFields,
  // notificationChannelFields,
} from './utils'

const {
  ALERT_IS_ENABLED,
  ALERT_URL,
  ALERT_BEARER_TOKEN,
  AGGREGATOR_API_MAX_HOURS_WITHOUT_RUN,
  SAP_CONNECTION_ALERT_THROTTLE_MINUTES,
  SAP_ORDER_UNSYNCED_ALERT_HOURS,
  SAP_RECON_UNSYNCED_ALERT_HOURS,
  SAP_RMA_UNSYNCED_ALERT_HOURS,
  SAP_INVENTORY_MAX_HOURS_WITHOUT_RUN,
  FRESH_INVENTORY_MAX_HOURS_WITHOUT_RUN,
  INSTASHOP_LASTMILE_DELAY_ALERT_WINDOW,
  NOTIFICATION_FALLBACK_THROTTLE_MINUTES,
} = configurationsFields

// Channel on/off settings are hidden for now; only the alert service sends
// const { NOTIFICATION_CHANNELS } = configRootFields

/**
 * Root-level timing settings. Blank keeps whatever the backend already has;
 * the placeholder shows the backend default.
 */
const TIMING_FIELDS: {
  field: { key: string; label: string }
  placeholder: string
  hintText: string
}[] = [
  {
    field: AGGREGATOR_API_MAX_HOURS_WITHOUT_RUN,
    placeholder: '1',
    hintText:
      'Hours a store may go without an Aggregator API run before it is reported. Also controls how often a still-failing store is re-reported. Default 1.',
  },
  {
    field: SAP_CONNECTION_ALERT_THROTTLE_MINUTES,
    placeholder: '30',
    hintText:
      'Minimum gap between repeat "cannot reach SAP" alerts. Shorter than the SAP sync-failure gap because an outage blocks every order. Default 30.',
  },
  {
    field: SAP_ORDER_UNSYNCED_ALERT_HOURS,
    placeholder: '4',
    hintText:
      'Hours an order may stay unsynced to SAP before the hourly sweep reports it. Scoped to the current day. Default 4.',
  },
  {
    field: SAP_RECON_UNSYNCED_ALERT_HOURS,
    placeholder: '4',
    hintText:
      'Same threshold for reconciliations still unsynced to SAP. Default 4.',
  },
  {
    field: SAP_RMA_UNSYNCED_ALERT_HOURS,
    placeholder: '4',
    hintText: 'Same threshold for RMAs still unsynced to SAP. Default 4.',
  },
  {
    field: SAP_INVENTORY_MAX_HOURS_WITHOUT_RUN,
    placeholder: '5',
    hintText:
      'Hours SAP may go without sending us inventory before it is reported. SAP pushes this data to us, so silence is the only symptom. Default 5.',
  },
  {
    field: FRESH_INVENTORY_MAX_HOURS_WITHOUT_RUN,
    placeholder: '5',
    hintText:
      'Hours the Fresh inventory pull may go without running before it is reported. Default 5.',
  },
  {
    field: INSTASHOP_LASTMILE_DELAY_ALERT_WINDOW,
    placeholder: '45',
    hintText:
      'Minutes a failed Lastmile order may wait for its retry before the order is reported as delayed. Reported even if the retry then succeeds. Default 45.',
  },
  {
    field: NOTIFICATION_FALLBACK_THROTTLE_MINUTES,
    placeholder: '60',
    hintText:
      'Minimum gap between "channel is failing" notices for the same channel, so a channel that stays down does not flood the others. Default 60.',
  },
]

type Props = {
  configuration?: ConfigurationWithId
}

const AlertConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<AlertConfigFormFields>({
    resolver: yupResolver(alertConfigSchema),
    defaultValues: {
      alert: {
        isEnabled: configuration?.alert?.isEnabled ?? false,
        url: configuration?.alert?.url ?? '',
        bearerToken: configuration?.alert?.bearerToken ?? '',
      },
      // Missing keys mean "on", matching the backend default
      // notificationChannels: {
      //   alert: configuration?.notificationChannels?.alert ?? true,
      //   sms: configuration?.notificationChannels?.sms ?? true,
      //   email: configuration?.notificationChannels?.email ?? true,
      //   fallback: configuration?.notificationChannels?.fallback ?? true,
      // },
      ...Object.fromEntries(
        ALERT_NUMERIC_FIELD_KEYS.map((key) => [key, configuration?.[key]]),
      ),
    },
  })

  const [loading, setLoading] = useState(false)

  const isEnabled = watch('alert.isEnabled')

  const handleSave = async (data: AlertConfigFormFields) => {
    const configToast = toast.loading('Updating alert config...')
    setLoading(true)
    try {
      // Blank timing boxes are left out so the stored value is kept
      const timing = Object.fromEntries(
        ALERT_NUMERIC_FIELD_KEYS.filter(
          (key) => data[key] !== undefined && data[key] !== null,
        ).map((key) => [key, data[key]]),
      )

      await updateAlertConfig({
        alert: buildAlertPayload(data.alert),
        // notificationChannels: data.notificationChannels,
        ...timing,
      })

      toast.success('Alert config updated successfully.', { id: configToast })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update alert config.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* ================= ALERT SERVICE ================= */}
      <h3 className="title mt-4">Alert Service</h3>

      <FormLayout>
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">
              {ALERT_IS_ENABLED.label}
            </p>
            <p className="text-xs text-gray-500">
              Master switch. When off, no failure notification goes out on any
              channel (alert, email or SMS); it is only logged.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                isEnabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isEnabled ? 'Active' : 'Inactive'}
            </span>

            <Controller
              name="alert.isEnabled"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch checked={!!value} onChange={onChange} />
              )}
            />
          </div>
        </div>

        <div className="mb-5 border-t" />

        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            register={register}
            name="alert.url"
            label={ALERT_URL.label}
            autoComplete="off"
            placeholder="https://..."
            error={getNestedErrorMessage(errors, ALERT_URL)}
            hintText="Full endpoint the alert payload is POSTed to."
          />
          <TextInput
            type="password"
            register={register}
            name="alert.bearerToken"
            label={ALERT_BEARER_TOKEN.label}
            autoComplete="new-password"
            error={getNestedErrorMessage(errors, ALERT_BEARER_TOKEN)}
            hintText="Raw token only — do not type the word Bearer, the server adds that prefix. Leave blank to keep the saved token unchanged."
          />
        </div>
      </FormLayout>

      {/* ================= CHANNELS (hidden for now) =================
      <h3 className="title mt-4">Notification Channels</h3>

      <FormLayout>
        <p className="mb-4 text-xs text-gray-500">
          Used only while Alert Service above is on. Turning a channel off here
          stops it everywhere; OTP SMS are not affected.
        </p>

        <div className="divide-y">
          {notificationChannelFields.map(({ key, label, hintText }) => (
            <div
              key={key}
              className="flex items-center justify-between gap-6 py-3"
            >
              <div>
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-500">{hintText}</p>
              </div>

              <Controller
                name={
                  `${NOTIFICATION_CHANNELS}.${key}` as 'notificationChannels.alert'
                }
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch checked={!!value} onChange={onChange} />
                )}
              />
            </div>
          ))}
        </div>
      </FormLayout>
      */}

      {/* ================= TIMING ================= */}
      <h3 className="title mt-4">Alert Timing</h3>

      <FormLayout>
        <div className="grid gap-x-10 gap-y-3 mid-md:grid-cols-3">
          {TIMING_FIELDS.map(({ field, placeholder, hintText }) => (
            <TextInput
              key={field.key}
              type="number"
              min={1}
              register={register}
              name={field.key as keyof AlertConfigFormFields}
              label={field.label}
              placeholder={placeholder}
              error={(errors as Record<string, any>)?.[field.key]?.message}
              hintText={hintText}
            />
          ))}
        </div>
      </FormLayout>

      <div className="mt-5 flex items-center gap-3">
        <Button loading={loading} buttonText="Save" className="w-32" />
      </div>
    </form>
  )
}

export default AlertConfigurationForm
